import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Card from './Card';
import Button from './Button';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  linkTo: string;
  delay?: number;
}

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ theme }) => theme.space.xl};
  height: 100%;
  position: relative;
  overflow: hidden;
  border-bottom: 3px solid transparent;
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
    border-bottom: 3px solid ${({ theme }) => theme.colors.accent};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: ${({ theme }) => theme.colors.gradient.accent};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
`;

const IconWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.borderRadius.blob};
  background: ${({ theme }) => theme.colors.gradient.accent};
  color: ${({ theme }) => theme.colors.dark};
  font-size: 2rem;
  margin-bottom: ${({ theme }) => theme.space.lg};
  box-shadow: ${({ theme }) => theme.shadows.neon};
  position: relative;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: ${({ theme }) => theme.borderRadius.blob};
    background: ${({ theme }) => theme.colors.gradient.neon};
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 0.5;
  }
`;

const Title = styled(motion.h3)`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.accent};
    transition: width 0.3s ease;
  }
`;

const Description = styled(motion.p)`
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: ${({ theme }) => theme.space.lg};
  flex-grow: 1;
  line-height: 1.7;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const ServiceCard: React.FC<ServiceCardProps> = ({
  icon,
  title,
  description,
  linkTo,
  delay = 0,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ type: 'spring', stiffness: 50, damping: 15, delay }}
      whileHover={{ y: -10, transition: { duration: 0.3 } }}
    >
      <StyledCard elevation="medium" hoverEffect>
        <IconWrapper
          initial={{ scale: 0.8, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 100, delay: delay + 0.2 }}
          whileHover={{ 
            rotate: 5, 
            scale: 1.1,
            transition: { duration: 0.3 }
          }}
        >
          {icon}
        </IconWrapper>
        <Title
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ type: 'spring', stiffness: 100, delay: delay + 0.3 }}
        >
          {title}
        </Title>
        <Description
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 50, delay: delay + 0.4 }}
        >
          {description}
        </Description>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 50, delay: delay + 0.5 }}
        >
          <Button variant="outline" as="a" href={linkTo}>
            Learn More
          </Button>
        </motion.div>
      </StyledCard>
    </motion.div>
  );
};

export default ServiceCard;
